import { useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  Flex
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";

export default function SignupSuccess() {
  const { width, height } = useWindowSize();
  const [params] = useSearchParams();
  const referrerId = params.get("ref");
  const hasFired = useRef(false);

  useEffect(() => {
    if (!hasFired.current) {
      if (referrerId) {
        window?.gtag?.("event", "referral_signup_success", {
          referrer_id: referrerId,
        });
      }
      window?.gtag?.("event", "signup_success_screen_viewed");
      hasFired.current = true;
    }
  }, [referrerId]);

  return (
    <>
      <Helmet>
        {/* BASIC SEO */}
        <title>Signup Successful | postcode.coupons</title>
        <meta
          name="description"
          content="Your postcode.coupons account has been created successfully. Access your dashboard and start exploring your advertising tools."
        />
        <link rel="canonical" href="https://postcode.coupons/signup/success" />

        {/* OPEN GRAPH */}
        <meta property="og:title" content="Signup Successful" />
        <meta
          property="og:description"
          content="Your postcode.coupons account has been created successfully. Access your dashboard and start exploring your advertising tools."
        />
        <meta property="og:image" content="https://postcode.coupons/og/homepage.png" />
        <meta property="og:url" content="https://postcode.coupons/signup/success" />
        <meta property="og:type" content="website" />

        {/* TWITTER */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Signup Successful" />
        <meta
          name="twitter:description"
          content="Your postcode.coupons account has been created successfully. Access your dashboard and start exploring your advertising tools."
        />
        <meta name="twitter:image" content="https://postcode.coupons/og/homepage.png" />

        {/* STRUCTURED DATA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Signup Success",
            url: "https://postcode.coupons/signup/success",
            description:
              "Your postcode.coupons account has been created successfully. Access your dashboard and start exploring your advertising tools."
          })}
        </script>
      </Helmet>

      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg="green.50"
        position="relative"
      >
        <Confetti
          width={width}
          height={height}
          numberOfPieces={300}
          recycle={false}
        />

        <Box
          bg="white"
          p={8}
          rounded="md"
          shadow="lg"
          textAlign="center"
          maxW="md"
          zIndex={1}
        >
          <Heading size="lg" mb={4}>
            🎉 Welcome to Postcode.Coupons!
          </Heading>

          <Text mb={6}>
            Your account has been created successfully. You’ve unlocked your
            dashboard and bonus coupons.
          </Text>

          <Button as={Link} to="/dashboard" colorScheme="green" size="lg">
            Go to Dashboard
          </Button>
        </Box>
      </Flex>
    </>
  );
}
